<template>
  <CardWrapper
    class="make-topup-widget"
    data-test-id="make-topup-widget"
    :title="i18n.topup_refill()"
  >
    <template v-if="accountVerified">
      <div
        :class="[
          'make-topup-widget__step',
          { 'active': checkActiveStep(1) }
        ]"
        data-test-id="make-topup-widget-first-step"
      >
        <StepTitle
          stepNumber="1"
          @click.native="setCurrentStep(1)"
        >
          <div class="make-topup-widget__step-title">
            <p data-test-id="make-topup-widget-first-step-title">
              {{ i18n.lbl_choose_asset() }}
            </p>
            <p
              v-show="selectedAsset && !checkActiveStep(1)"
              data-test-id="make-topup-widget-selected-asset"
            >
              {{ selectedAsset }}
            </p>
          </div>
        </StepTitle>
        <AccordeonWrapper v-show="checkActiveStep(1)">
          <div
            class="make-topup-widget__step-inner"
            data-test-id="make-topup-widget-first-step-container"
          >
            <SelectWrapper
              :options="cryptoAssets"
              :selected="selectedAsset"
              data-test-id="make-topup-widget-asset-select"
              @setSelected="setSelectedAsset($event)"
            />
          </div>
        </AccordeonWrapper>
      </div>
      <div
        :class="[
          'make-topup-widget__step',
          { 'active': checkActiveStep(2) }
        ]"
        data-test-id="make-topup-widget-second-step"
      >
        <StepTitle
          stepNumber="2"
          @click.native="setCurrentStep(2)"
        >
          {{  i18n.lbl_one_time_wallet_address()  }}
          <md-button
            v-show="selectedAsset && checkActiveStep(2)"
            class="md-icon-button md-raised make-topup-widget__tooltip-button"
            data-test-id="make-topup-widget-tooltip-btn"
          >
            <md-icon md-src="/static/img/info-icon.svg" />
            <md-tooltip
              md-direction="bottom"
              class="custom-tooltip"
              data-test-id="make-topup-widget-tooltip"
            >
              <template v-if="minAmounts[selectedAssetWithoutSpace]">
                {{ i18n.topup_how_much({
                    asset: selectedAsset,
                    value: minAmounts[selectedAssetWithoutSpace]
                  })
                }}.
                {{ i18n.topup_crypto_minimal() }}
              </template>
              <template v-else>
                {{  i18n.topup_network_fee()  }}
              </template>
            </md-tooltip>
          </md-button>
        </StepTitle>
        <AccordeonWrapper v-if="checkActiveStep(2)">
          <div
            v-if="currentQr"
            class="make-topup-widget__step-inner"
            data-test-id="make-topup-widget-second-step-container"
          >
            <p
              class="make-topup-widget__message"
              data-test-id="make-topup-widget-timer-message"
            >
              {{ i18n.lbl_wait_30() }}
            </p>
            <div class="make-topup-widget__address">
              <div>
                <qrcode
                  :style="'width: 150px; height: 150px; overflow: hidden; margin: 0px auto !important;'"
                  :text="currentQr"
                  :size="150"
                  color="#3f4244"
                  align="center"
                  data-test-id="make-topup-widget-qrcode"
                />
              </div>
              <div class="make-topup-widget__address-hash">
                <div class="timer-row">
                  <Timer
                    data-test-id="make-topup-widget-timer"
                    :showMessage="false"
                  />
                </div>
                <div class="hash-row">
                  <p
                    id="one-time-wallet-hash"
                    data-test-id="make-topup-widget-wallet-hash"
                  >
                    {{ currentQr }}
                  </p>
                  <md-button
                    class="md-icon-button md-raised make-topup-widget__copy-button"
                    id="clipboard-btn"
                    data-clipboard-target="#one-time-wallet-hash"
                    data-test-id="make-topup-widget-copy-hash-btn"
                    @click="copyHash"
                  >
                    <md-icon>content_copy</md-icon>
                  </md-button>
                </div>
                <button
                  v-if="createBtn"
                  type="button"
                  class="recreate-btn"
                  data-test-id="make-topup-widget-recreate-wallet-btn"
                  @click="resetQrByBtn"
                >
                  {{ i18n.lbl_create_btn() }}
                </button>
              </div>
            </div>
          </div>
          <div
            v-else
            class="make-topup-widget__step-inner loading"
            data-test-id="make-topup-widget-loading"
          >
            <p>{{ i18n.lbl_loading() }}</p>
          </div>
        </AccordeonWrapper>
      </div>
    </template>
    <div
      v-else
      class="make-topup-widget__not-verified"
      data-test-id="make-topup-widget-not-verified"
    >
      <p v-html="getMessage()" @click="handleGoToVerification"></p>
    </div>
  </CardWrapper>
</template>

<script>
  import { i18n } from '@js/i18n'
  import { mapGetters, mapMutations, mapActions } from 'vuex'
  import { vuexTypes } from '@vuex/types'
  import { CRYPTO_CURRENCYS, CRYPTO_CURRENCYS_USDT, QR_KEY_BY_NETWORK, getTokenNetwork, isNetworkToken, isCryptoToken, formatTokenCode, sortAssetCodes } from '@utils/consts'
  import { limitsService } from '@api/limits.service'
  import CurrencyRound from '@com/mixins/currency.round.mixin'
  import UiMixin from '@com/mixins/ui.mixin'
  import { EventDispatcher } from '@js/events/event_dispatcher'

  import Qrcode from 'vue-qrcode-component'
  import Clipboard from 'clipboard'
  import CardWrapper from '@com/UI/CardWrapper.vue'
  import StepTitle from '@com/UI/StepTitle.vue'
  import SelectWrapper from '@com/UI/SelectWrapper.vue'
  import AccordeonWrapper from '@com/UI/AccordeonWrapper.vue'
  import Timer from '../../timer/Timer'

  export default {
    name: 'MakeTopupWidget',
    components: {
      CardWrapper,
      StepTitle,
      SelectWrapper,
      AccordeonWrapper,
      Timer,
      Qrcode
    },
    mixins: [CurrencyRound, UiMixin],
    data () {
      return {
        CRYPTO_CURRENCYS,
        i18n,
        currentStep: 1,
        selectedAsset: '',
        minAmounts: {
          USDTTRC20: '10.00',
          USDTERC20: '10.00',
          USDTBEP20: '10.00'
        },
        clipboard: null
      }
    },
    computed: {
      ...mapGetters([
        vuexTypes.accountVerified,
        vuexTypes.isProductionEnv,
        vuexTypes.userWithdrawableTokens
      ]),
      cryptoAssets () {
        const codes = this.userWithdrawableTokens
          .map(token => token.code)
          .filter(isNetworkToken)

        // Fall back to the legacy set until balances are loaded
        const result = codes.length ? codes : Object.values(CRYPTO_CURRENCYS_USDT)

        return sortAssetCodes(result).map(formatTokenCode)
      },
      timer () {
        return this.$store.state.timer.timer
      },
      createBtn () {
        return this.$store.state.timer.btn
      },
      selectedAssetWithoutSpace () {
        return this.selectedAsset && this.selectedAsset.replace(' ', '')
      },
      currentQr () {
        try {
          const qrCodes = this.$store.state.timer.qrCodes
          if (!qrCodes) return null
          // Deposit addresses are per-network: all tokens of a network share one address
          const network = getTokenNetwork(this.selectedAssetWithoutSpace)
          return (network && qrCodes[QR_KEY_BY_NETWORK[network]]) || null
        } catch (e) {
          console.error(e)
          return null
        }
      }
    },
    methods: {
      ...mapActions({
        startTimer: 'startTimer'
      }),
      ...mapMutations({
        clearTimer: 'clearTimer'
      }),
      setCurrentStep (step) {
        if (step === 2 && !this.selectedAsset) return
        this.currentStep = step
      },
      checkActiveStep (step) {
        return this.currentStep === step
      },
      setSelectedAsset (asset) {
        this.selectedAsset = asset
        this.setCurrentStep(2)
      },
      async resetQr () {
        this.clearTimer()
        await this.startTimer({
          reactivateWallet: false,
          needCheckForm: false
        })
      },
      async resetQrByBtn (e) {
        // Ripple эффект
        this.addRippleOnBtnClick(e)

        this.clearTimer()
        await this.startTimer({
          reactivateWallet: true,
          needCheckForm: false
        })
      },
      copyHash () {
        EventDispatcher.dispatchShowSuccessEvent(this.i18n.notif_one_time_wallet_copied())
      },
      setClipboard () {
        const btn = document.querySelector('#clipboard-btn')
        if (!btn) return
        this.clipboard = new Clipboard(btn)
      },
      getMessage () {
        const link = (placeholder) => `
          <span
            class="verification__link"
            data-test-id="last-transactions-widget-empty-table-dialog-link"
            data-action="go-to-verification"
          >
            ${placeholder}
        </span>`

        return i18n.verification_needed_msg()
          .split(' ')
          .map((str, i) => {
            return i === 1 ? link(str) : str
          })
          .join(' ')
      },
      handleGoToVerification (e) {
        const target = e.target.closest('[data-action="go-to-verification"]')
        if (target) {
          this.$router.push('/verification')
        }
      },
      async loadSelectedAssetMinAmount (asset) {
        const minAmount = await limitsService.loadDepositMinAmount(asset)
        if (minAmount) this.$set(this.minAmounts, asset, minAmount)
      }
    },
    watch: {
      cryptoAssets (list) {
        // The list flips once balances load; drop a selection that is no longer offered
        if (this.selectedAsset && !list.includes(this.selectedAsset)) {
          this.selectedAsset = ''
          this.currentStep = 1
        }
      },
      async selectedAsset () {
        if (isCryptoToken(this.selectedAssetWithoutSpace) && !this.timer) {
          this.resetQr()
        }
        await this.loadSelectedAssetMinAmount(this.selectedAssetWithoutSpace)
      },
      async currentQr () {
        if (this.checkActiveStep(2)) {
          await this.$nextTick()
          this.setClipboard()
        }
      }
    },
    async created () {
      if (!this.isProductionEnv) {
        this.minAmounts.USDTTRC20 = '1.00'
        this.minAmounts.USDTERC20 = '1.00'
        this.minAmounts.USDTBEP20 = '1.00'
      }
    }
  }
</script>

<style lang="scss" scoped>
.make-topup-widget {
  &__step {
    /deep/ .step-title {
      cursor: pointer !important;
    }
  }
}
</style>

<style lang="scss">
@import '~scss/variables';

.make-topup-widget {
  &__step {
    border-bottom: 1px solid $grey;
    padding: 20px 0;

    &.active {
      .make-topup-widget__step-inner {
        padding: 10px 0 0 40px;
      }
    }

    &:last-child {
      border-bottom: none;
    }

    &-title {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 1rem;
      font-weight: 400;

      p {
        font-size: 18px;
        font-weight: 700;
      }
    }
  }

  &__step-inner {
    &.loading {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 50px;
      padding: 10px 0 0 0 !important;

      p {
        font-size: 18px;
        font-weight: 600;
      }
    }
  }

  &__address {
    display: flex;
    gap: 20px;
    width: 100%;

    &-hash {
      display: flex;
      flex-direction: column;
      gap: 10px;
      max-width: calc(100% - 165px);
      z-index: 0;

      .hash-row {
        display: flex;
        align-items: center;

        p {
          font-size: 16px;
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      .timer-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .recreate-btn {
        height: 40px;
        padding: 10px;
        font-size: 1rem;
        font-weight: 600;
        border-radius: 10px;
        max-width: max-content;
        background: $color-graphite;
        color: $color-mint;
        cursor: pointer;
        border: 1px solid transparent;
        position: relative;
        transition: background 0.3s;
        overflow: hidden;

        .ripple {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.5);
          transform: scale(0);
          animation: ripple-animation 0.6s linear;
          pointer-events: none;
          z-index: 1000;
        }
      }
    }
  }

  &__message {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 500;
    padding-bottom: 14px;
  }

  &__tooltip-button,
  &__copy-button {
    background: $color-graphite !important;
    width: 30px;
    height: 30px;
    min-width: 30px;
    flex-shrink: 1;

    .md-icon {
      color: $color-mint !important;
      font-size: 16px !important;


      svg {
        fill: $color-mint !important;
      }
    }
  }

  &__tooltip-button {
    .md-icon {
      width: 20px;
      min-width: 20px;
      height: 20px;
    }
  }

  &__copy-button {
    .md-icon {
      font-size: 14px !important;
    }
  }

  &__not-verified {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    height: 100%;
    font-size: 18px;
    text-align: center;
    max-width: 80%;
    margin: 0 auto;

    .verification__link {
      text-transform: lowercase;
      border-bottom: 2px solid $color-mint;
      cursor: pointer;
      color: $color-mint;
      transition: border-bottom 0.1s ease-in-out;

      &:hover {
        border-bottom: 2px solid transparent;
      }
    }
  }
}

.md-tooltip.custom-tooltip {
  font-size: 16px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  max-width: 280px;
  width: 100%;
  white-space: initial;
  height: max-content;
}
</style>



// WEBPACK FOOTER //
// src/vue/app/dashboard/widgets/MakeTopupWidget.vue